import {
    useImperativeHandle,
    useCallback,
    forwardRef,
    useEffect,
    useState,
    useRef
} from "react";
import type AddParticipantSheetProps from "./type";
import type {
    AddParticipantSheetRef
} from "./type";
import {
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    SelectSheet
} from "ncore-ui-kit";
import type {
    GetAllUsersForAdminObjectType
} from "../../../../services/restAPI/actions/getAllUsersForAdmin/type";
import RESTServiceContext from "../../../../services/restAPI/context";

export type SelectedItem = {
    __title: string;
    __key: string;
};

interface IBottomSheetRef {
    setIsContentReady: (status: boolean) => void;
    close: () => void;
    open: () => void;
}

const AddParticipantSheet = forwardRef<AddParticipantSheetRef, AddParticipantSheetProps>(({
    onParticipantAdded,
    participants,
    eventSubID,
    eventID
}, ref) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();
    const bottomSheetRef = useRef<IBottomSheetRef>(null);

    const [
        isActive,
        setIsActive
    ] = useState(false);
    const [
        isLoading,
        setIsLoading
    ] = useState(false);
    const [
        isMoreLoading,
        setIsMoreLoading
    ] = useState(false);
    const [
        searchText,
        setSearchText
    ] = useState("");

    const [
        users,
        setUsers
    ] = useState<(GetAllUsersForAdminObjectType & SelectedItem)[]>([]);
    const [
        mainSelectedItems,
        setMainSelectedItems
    ] = useState<(GetAllUsersForAdminObjectType & SelectedItem)[]>([]);

    const [
        page,
        setPage
    ] = useState(1);
    const [
        hasMore,
        setHasMore
    ] = useState(true);

    useImperativeHandle(ref, () => ({
        setIsContentReady: (status: boolean) => {
            bottomSheetRef.current?.setIsContentReady(status);
        },
        close: () => {
            bottomSheetRef.current?.close();
            setIsActive(false);
        },
        open: () => {
            bottomSheetRef.current?.open();
            setIsActive(true);
        }
    }));

    const fetchUsers = useCallback(async (currentPage: number, currentSearch: string, isLoadMore = false, accumulatedCount = 0) => {
        if (isLoadMore) {
            setIsMoreLoading(true);
        } else {
            setIsLoading(true);
        }

        try {
            const res = await RESTServiceContext.action("getAllUsersForAdmin", {
                search: currentSearch,
                page: currentPage
            });

            if (res.code === 200 && res.payload) {
                const formattedUsers = res.payload.map((u: GetAllUsersForAdminObjectType) => ({
                    ...u,
                    __title: `${u.firstName} ${u.lastName} - ${u.phoneNumber}`,
                    __key: u._id
                }));

                const filteredUsers = formattedUsers.filter(usr => {
                    const isAlreadyAdded = participants?.some(p => (p.userID || p.userInfo?.id) === usr._id);
                    return !isAlreadyAdded;
                });

                if (isLoadMore) {
                    setUsers(prev => {
                        const existingIds = new Set(prev.map(p => p.__key));
                        const newUsers = filteredUsers.filter(u => !existingIds.has(u.__key));
                        return [
                            ...prev,
                            ...newUsers
                        ];
                    });
                } else {
                    setUsers(filteredUsers);
                }

                if (res.totalCount !== undefined) {
                    const currentFetchedTotal = ((currentPage - 1) * 20) + res.payload.length;
                    setHasMore(currentFetchedTotal < res.totalCount);

                    const newAccumulatedCount = accumulatedCount + filteredUsers.length;
                    if (currentFetchedTotal < res.totalCount && newAccumulatedCount < 15) {
                        const nextPage = currentPage + 1;
                        setPage(nextPage);
                        await fetchUsers(nextPage, currentSearch, true, newAccumulatedCount);
                    }
                } else {
                    setHasMore(res.payload.length === 20);

                    const newAccumulatedCount = accumulatedCount + filteredUsers.length;
                    if (res.payload.length === 20 && newAccumulatedCount < 15) {
                        const nextPage = currentPage + 1;
                        setPage(nextPage);
                        await fetchUsers(nextPage, currentSearch, true, newAccumulatedCount);
                    }
                }
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("global-error"),
                type: "danger"
            });
        } finally {
            setIsLoading(false);
            setIsMoreLoading(false);
        }
    }, [
        participants,
        localize,
        users.length
    ]);

    useEffect(() => {
        if (isActive) {
            const timeout = setTimeout(() => {
                setPage(1);
                fetchUsers(1, searchText, false);
            }, 500);
            return () => clearTimeout(timeout);
        }
        return () => {};
    }, [
        searchText,
        isActive
    ]);

    const selectObject = (selectedItem: SelectedItem | (GetAllUsersForAdminObjectType & SelectedItem)) => {
        const fullItem = selectedItem as (GetAllUsersForAdminObjectType & SelectedItem);
        const isAlreadySelected = mainSelectedItems.findIndex(sItem => sItem.__key === fullItem.__key);
        const _selectedItems = [...mainSelectedItems];

        if (isAlreadySelected !== -1) {
            _selectedItems.splice(isAlreadySelected, 1);
        } else {
            _selectedItems.push(fullItem);
        }
        setMainSelectedItems(_selectedItems);
    };

    const selectAll = () => {
        setMainSelectedItems(users);
    };

    const cleanAll = () => {
        setMainSelectedItems([]);
    };

    const cancel = () => {
        cleanAll();
        bottomSheetRef.current?.close();
        setIsActive(false);
        setSearchText("");
    };

    const ok = async () => {
        if (!eventID || !eventSubID || mainSelectedItems.length === 0) {
            NCoreUIKitToast.open({
                title: localize("threet-please-select-member"),
                type: "warning"
            });
            return;
        }

        setIsLoading(true);
        try {
            let successCount = 0;
            const promises = mainSelectedItems.map(usr => {
                return RESTServiceContext.action("threeTAddParticipantManually", {
                    phoneNumber: usr.phoneNumber,
                    userID: usr._id,
                    eventSubID,
                    eventID
                }).then(response => {
                    if (response.code === 200 || response.code === 201) {
                        successCount++;
                    }
                }).catch(() => {});
            });

            await Promise.all(promises);

            if (successCount > 0) {
                NCoreUIKitToast.open({
                    title: localize("threet-member-added-success"),
                    type: "success"
                });
                onParticipantAdded();
                cancel();
            } else {
                NCoreUIKitToast.open({
                    title: localize("threet-event-locked-error"),
                    type: "danger"
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("global-error"),
                type: "danger"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const onMoreLoad = () => {
        if (!isLoading && !isMoreLoading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchUsers(nextPage, searchText, true);
        }
    };

    return <SelectSheet
        title={localize("threet-add-member-manual-title")}
        mainSelectedItems={mainSelectedItems}
        selectBoxKey="add-participant-sheet"
        keyExtractor={(item) => item.__key}
        setIsMoreLoading={setIsMoreLoading}
        bottomSheetRef={bottomSheetRef}
        setSearchText={setSearchText}
        isMoreLoading={isMoreLoading}
        bottomSheetProps={{
            isWorkWithPortal: false,
            scrollViewStyle: {
                minHeight: 400
            }
        }}
        setIsLoading={setIsLoading}
        selectObject={selectObject}
        isWorkWithRealtime={false}
        setIsActive={setIsActive}
        isMultipleSelect={true}
        searchText={searchText}
        onMoreLoad={onMoreLoad}
        selectAll={selectAll}
        isLoading={isLoading}
        searchedData={users}
        isSearchable={true}
        cleanAll={cleanAll}
        isActive={isActive}
        isShowTools={true}
        cancel={cancel}
        maxChoice={-1}
        data={users}
        ok={ok}
    />;
});

export default AddParticipantSheet;
